/** @jsx isolateComponent */
import React, { ReactNode } from 'react';
import {
  Animation,
  ColorName,
  Colors,
  ShadowName,
  Shadows,
} from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type SurfaceProps = {
  /**
   * The color of the surface's background.
   *
   * @default 'transparent'
   * */
  backgroundColor?: ColorName;
  /**
   * The color of border to render around the surface.
   * */
  borderColor?: ColorName;
  /**
   * Child node to render inside the surface.
   * */
  children: ReactNode;
  /**
   * Applies a `box-shadow` with the specified shadow.
   *
   * @default 'elevation0'
   * */
  shadow?: ShadowName;
};

const Styles = labelStyles(__filename, {
  root: (
    backgroundColor: ColorName,
    shadow: ShadowName,
    borderColor?: ColorName
  ) => [
    {
      borderRadius: 10,
      transitionProperty: 'box-shadow, border-color, background-color',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
      alignItems: 'stretch',
      flex: 1,
      maxWidth: '100%',
    },
    backgroundColor &&
      withModifier(backgroundColor, {
        backgroundColor: Colors[backgroundColor],
      }),
    borderColor &&
      withModifier(`border-${borderColor}`, {
        border: `1px solid ${Colors[borderColor]}`,
      }),
    shadow &&
      withModifier('hasShadow', {
        boxShadow: Shadows[shadow],
      }),
  ],
});
/**
 * Renders a Surface component. Surfaces represent content blocks, they are useful
 * for creating visual emphasis of content on a page. <br />
 * The Surface component acts as a "wrapping" component
 *
 * Tags: core-ui
 */
const Surface: React.FC<SurfaceProps> = props => {
  const {
    backgroundColor = 'transparent',
    shadow = 'elevation0',
    borderColor,
  } = props;
  return (
    <div css={Styles.root(backgroundColor, shadow, borderColor)}>
      {props.children}
    </div>
  );
};

export default Surface;
